import type { LucideIcon } from "lucide-react";
import {
  Compass,
  Stethoscope,
  Video,
  BookHeart,
  Home,
  LayoutDashboard,
  Settings,
  BookOpen,
  Bell,
  CalendarCheck,
  Users,
  ShieldCheck,
  Mail,
  HelpCircle,
  Sparkles,
} from "lucide-react";

/**
 * Canonical icon for each top-level feature/route in the app.
 *
 * Icons here mirror the iconography used on the corresponding feature page,
 * so any new nav item (header, mobile sheet, footer, dashboard quick-links)
 * automatically gets the right icon by referencing its route. This keeps
 * branding consistent without hand-wiring icons in every nav surface.
 *
 * Add a new route here once and every nav surface picks it up.
 */
export const NAV_ICON_REGISTRY: Record<string, LucideIcon> = {
  "/": Home,
  "/how-it-works": Compass,
  "/symptom-sniffer": Stethoscope,
  "/getvideovet": Video,
  "/getpetpassport": BookHeart,
  "/my-dashboard": LayoutDashboard,
  "/vet-dashboard": Stethoscope,
  "/admin": ShieldCheck,
  "/settings": Settings,
  "/helpdesk": BookOpen,
  "/notifications": Bell,
  "/calendar": CalendarCheck,
  "/contact": Mail,
  "/about": Users,
  "/security": ShieldCheck,
  "/find-vets": Compass,
  "/vet-directory": Compass,
};

/** Fallback when no exact route match is registered. */
export const DEFAULT_NAV_ICON: LucideIcon = Sparkles;

/**
 * Resolve the canonical icon for a route.
 *
 * Lookup order:
 *   1. Exact match in NAV_ICON_REGISTRY (e.g. "/getvideovet")
 *   2. Longest registered prefix match (e.g. "/getvideovet/123" → Video)
 *   3. Explicit override passed in
 *   4. DEFAULT_NAV_ICON (Sparkles)
 */
export const getNavIcon = (path: string, override?: LucideIcon): LucideIcon => {
  // Guard against an override that was imported but ended up undefined
  // (e.g. typo, removed export, circular import during HMR).
  if (typeof override === "function" || typeof override === "object") {
    if (override) return override;
  }
  const exact = NAV_ICON_REGISTRY[path];
  if (exact) return exact;
  // Longest-prefix match for nested routes.
  const match = Object.keys(NAV_ICON_REGISTRY)
    .filter((key) => key !== "/" && path.startsWith(`${key}/`))
    .sort((a, b) => b.length - a.length)[0];
  const resolved = match ? NAV_ICON_REGISTRY[match] : undefined;
  // Final safety net: registry value could be undefined if someone added
  // a route key without a valid icon import.
  return resolved ?? DEFAULT_NAV_ICON;
};

/** Convenience for callers that just need the fallback icon. */
export { HelpCircle };
